import { Component, inject, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { map, Observable, of, Subject } from "rxjs";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { TranslateModule } from "@ngx-translate/core";
import { FMarketRowComponent } from "./f-market-row/f-market-row.component";
import { MarketOpsService } from "../market/market-ops.service";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { Router, RouterLink } from "@angular/router";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { AsyncPipe } from "@angular/common";
import { BlockOverlayComponent } from "../../../../components/block-overlay/block-overlay.component";
import { WhiteLabelConfigService } from "../../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";
import { BetConfigComponent } from "../market/market-runners/bet-config/bet-config.component";

@Component({
  selector: "app-fancy-market",
  imports: [
    TranslateModule,
    FMarketRowComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    AsyncPipe,
    RouterLink,
    BlockOverlayComponent,
    BetConfigComponent,
  ],
  templateUrl: "./fancy-market.component.html",
  styleUrl: "./fancy-market.component.scss",
})
export class FancyMarketComponent implements OnInit, OnDestroy {
  @Input() market: any;
  @Input() openDate: any;
  readonly destroy$ = new Subject<void>();
  updatedRunners = signal({});
  marketService = inject(MarketService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  liveUpdateService = inject(LiveUpdateService);
  placementService = inject(BetPlacementService);
  router = inject(Router);
  allowPlacement = true;
  timeDiffCheckInterval: any = null;

  constructor() {}

  get isMyMarketsPage() {
    return this.router.url.includes("content:my-markets") || this.router.url.includes("content:home");
  }

  ngOnInit() {
    if (this.market) {
      this.getMarketUpdates(this.market.topic);
      this.checkPreMatchPlacement();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timeDiffCheckInterval) clearInterval(this.timeDiffCheckInterval);
  }

  getMarketUpdates(marketId: string) {
    this.liveUpdateService
      .getData("market", { field: "topic", value: marketId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) this.handleMarketUpdate(data);
        },
      });
  }

  checkPreMatchPlacement() {
    const placementPermitted = this.wlConfigService.showFeature("preMatchPlacement");
    this.allowPlacement = !placementPermitted && this.openDate ? this.wlConfigService.isWithin45Minutes(this.openDate) : true;

    if (!placementPermitted && this.openDate) {
      this.timeDiffCheckInterval = setInterval(() => {
        // console.log(this.wlConfigService.isWithin45Minutes(this.openDate));
        this.allowPlacement = this.wlConfigService.isWithin45Minutes(this.openDate);
      }, 5000);
    }
  }

  showMarket(market: any): Observable<boolean> {
    if (!this.isMyMarketsPage) return of(true);

    return this.placementService.openBetsSubject.pipe(
      map((openBets: any[]) => {
        const fancyBetsSelectionIds = openBets.filter((bet) => bet.descriptionPrefix === "Fancy").map((bet) => bet.selectionId);
        return fancyBetsSelectionIds.includes(market?.selectionId);
      }),
    );
  }

  handleMarketUpdate(update: any) {
    if (update?.runners && update?.runners?.length > 0) {
      // console.log(this.router.url, update?.runners);
      if (!this.isMyMarketsPage) this.syncNewRunners(update?.runners);
      this.updatedRunners.set(this.marketOps.transformBmFancyRunners(update?.runners));
    }
  }

  syncNewRunners(updateRunners: any) {
    const existingIds = new Set(this.market?.runners.map((runner: any) => runner?.selectionId));

    const newRunners = updateRunners?.filter((runner: any) => !existingIds.has(runner.selectionId));

    if (newRunners.length > 0) {
      // console.log(newRunners);
      this.market = {
        ...this.market,
        runners: [...this.market.runners, ...newRunners],
      };
    }
  }

  getRunnerUpdate(selectionId: number) {
    const runners: any = this.updatedRunners();
    return runners?.[selectionId];
  }
}
